import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../config/firebaseConfig';
import { getDocs, collection } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import Pagination from 'react-bootstrap/Pagination';
import { Button, Card, Dropdown, Accordion } from 'react-bootstrap';
import { saveAs } from 'file-saver';

const YemekListesi = () => {
  const [yemekler, setYemekler] = useState([]);
  const [alisverisListesi, setAlisverisListesi] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [shoppingPage, setShoppingPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const yemeklerPerPage = 9; // 3x3 grid = 9 cards
  const shoppingItemsPerPage = 18; // 3 columns x 6 rows = 18 items

  // Haftanın gününü hesaplayan fonksiyon
  const getDayName = (tarih) => {
    try {
      const date = new Date(tarih);
      if (isNaN(date)) return 'Bilinmeyen Gün';
      const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
      return days[date.getDay()];
    } catch {
      return 'Bilinmeyen Gün';
    }
  };

  // Alışveriş listesi oluşturma fonksiyonu
  const generateAlisverisListesi = useCallback((yemekList) => {
    const tumMalzemeler = {};
    // Seçilen aya göre filtrele
    const filteredList = selectedMonth === 'all'
      ? yemekList
      : yemekList.filter((yemek) => yemek.tarih.includes(selectedMonth));

    filteredList.forEach((yemek) => {
      yemek.malzemeler.forEach((malzemeObj) => {
        // Only include unpurchased ingredients (satinAlindi: false or undefined)
        if (!malzemeObj.satinAlindi) {
          const normalizedMalzeme = malzemeObj.ad.toLowerCase().trim();
          tumMalzemeler[normalizedMalzeme] = (tumMalzemeler[normalizedMalzeme] || 0) + 1;
        }
      });
    });

    const liste = Object.entries(tumMalzemeler).map(([malzeme, miktar]) => ({
      malzeme: malzeme.charAt(0).toUpperCase() + malzeme.slice(1),
      miktar,
    }));

    // Miktara göre azalan sırayla sırala
    liste.sort((a, b) => b.miktar - a.miktar);

    setAlisverisListesi(liste);
    setShoppingPage(1); // Reset shopping list pagination
  }, [selectedMonth]);

  useEffect(() => {
    const loadYemekler = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'yemekListesi'));
        let yemekList = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const yemekTarih = data.tarih || 'Bilinmeyen Tarih';
          const yemekDate = new Date(yemekTarih);
          const today = new Date('2025-04-16'); // Current date as per instruction
          today.setHours(0, 0, 0, 0); // Reset time for comparison

          // Only include meals with dates on or after today
          if (!isNaN(yemekDate) && yemekDate >= today) {
            yemekList.push({
              id: doc.id,
              tarih: yemekTarih,
              yemekAdi: data.yemekAdi || 'İsimsiz Yemek',
              malzemeler: data.malzemeler || [],
              kategori: data.kategori || 'Genel',
              tarif: data.tarif || 'Tarif mevcut değil.',
            });
          }
        });

        // Tarihe göre artan sırayla sırala
        yemekList = yemekList.sort((a, b) => {
          const dateA = new Date(a.tarih);
          const dateB = new Date(b.tarih);
          return isNaN(dateA) || isNaN(dateB) ? 0 : dateA - dateB;
        });

        setYemekler(yemekList);
        generateAlisverisListesi(yemekList);
      } catch (error) {
        console.error('Yemek listesi yüklenirken hata:', error);
      }
    };

    loadYemekler();
  }, [generateAlisverisListesi]);

  // Ay değiştiğinde alışveriş listesini güncelle
  useEffect(() => {
    generateAlisverisListesi(yemekler);
  }, [yemekler, generateAlisverisListesi]);

  const filteredYemekler = selectedMonth === 'all'
    ? yemekler
    : yemekler.filter((yemek) => yemek.tarih.includes(selectedMonth));

  // Pagination for meals
  const indexOfLastYemek = currentPage * yemeklerPerPage;
  const indexOfFirstYemek = indexOfLastYemek - yemeklerPerPage;
  const currentYemekler = filteredYemekler.slice(indexOfFirstYemek, indexOfLastYemek);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredYemekler.length / yemeklerPerPage); i++) {
    pageNumbers.push(
      <Pagination.Item
        key={i}
        active={i === currentPage}
        onClick={() => paginate(i)}
      >
        {i}
      </Pagination.Item>
    );
  }

  // Pagination for shopping list
  const indexOfLastShoppingItem = shoppingPage * shoppingItemsPerPage;
  const indexOfFirstShoppingItem = indexOfLastShoppingItem - shoppingItemsPerPage;
  const currentShoppingItems = alisverisListesi.slice(indexOfFirstShoppingItem, indexOfLastShoppingItem);

  const shoppingPageNumbers = [];
  for (let i = 1; i <= Math.ceil(alisverisListesi.length / shoppingItemsPerPage); i++) {
    shoppingPageNumbers.push(
      <Pagination.Item
        key={i}
        active={i === shoppingPage}
        onClick={() => setShoppingPage(i)}
      >
        {i}
      </Pagination.Item>
    );
  }

  const copyAlisverisListesi = () => {
    const listeText = alisverisListesi
      .map((item) => `${item.malzeme}: ${item.miktar} adet`)
      .join('\n');
    navigator.clipboard.writeText(listeText);
    alert('Alışveriş listesi panoya kopyalandı!');
  };

  const downloadAlisverisListesi = () => {
    const listeText = alisverisListesi
      .map((item) => `${item.malzeme}: ${item.miktar} adet`)
      .join('\n');
    const blob = new Blob([listeText], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${selectedMonth === 'all' ? 'tum-aylar' : selectedMonth}-alisveris-listesi.txt`);
  };

  return (
    <div className="yemek-listesi-container">
      <Helmet>
        <title>Aylık Yemek Listesi | Lezzetli ve Pratik Yemek Planları</title>
        <link rel="canonical" href="https://www.hacialikara.com/yemek-listesi" />
        <meta
          name="description"
          content="Aylık yemek listemizi inceleyin ve otomatik oluşturulan alışveriş listesi ile kolayca hazırlık yapın."
        />
      </Helmet>

      <div className="yemek-listesi-section-title text-center mb-5">
        <h2>Aylık Yemek Planı</h2>
        <div className="yemek-listesi-title-shape">
          <svg viewBox="0 0 200 20" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M 0,10 C 40,0 60,20 100,10 C 140,0 160,20 200,10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </div>
        <p className="yemek-listesi-lead">Lezzetli yemek planlarınızı kolayca oluşturun ve alışverişi hızlandırın.</p>
      </div>

      <div className="yemek-listesi-filter mb-4 d-flex justify-content-center">
        <Dropdown>
          <Dropdown.Toggle variant="outline-success" id="month-filter">
            {selectedMonth === 'all' ? 'Tüm Aylar' : selectedMonth}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setSelectedMonth('all')}>Tüm Aylar</Dropdown.Item>
            <Dropdown.Item onClick={() => setSelectedMonth('2025-04')}>Nisan 2025</Dropdown.Item>
            <Dropdown.Item onClick={() => setSelectedMonth('2025-05')}>Mayıs 2025</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <section className="yemek-listesi-meals">
        {currentYemekler.length > 0 ? (
          <div className="row row-cols-1 row-cols-md-3 g-4">
            {currentYemekler.map((yemek) => (
              <div key={yemek.id} className="col">
                <Card className="yemek-listesi-card h-100 shadow-sm">
                  <Card.Header className="yemek-listesi-card-header">
                    {getDayName(yemek.tarih)} - {yemek.tarih}
                  </Card.Header>
                  <Card.Body>
                    <Card.Title className="yemek-listesi-card-title">{yemek.yemekAdi}</Card.Title>
                    <Card.Text className="yemek-listesi-card-text">
                      <strong>Kategori:</strong> {yemek.kategori} <br />
                      <strong>Malzemeler:</strong> {yemek.malzemeler.map(m => m.ad).join(', ')}
                    </Card.Text>
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Tarifi Göster</Accordion.Header>
                        <Accordion.Body className="yemek-listesi-tarif">
                          {yemek.tarif}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <p className="yemek-listesi-empty text-center">Seçilen ayda yemek planı bulunmuyor.</p>
        )}
        {filteredYemekler.length > yemeklerPerPage && (
          <div className="yemek-listesi-pagination d-flex justify-content-center mt-5">
            <Pagination className="yemek-listesi-pagination-custom">
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))}
                disabled={currentPage === 1}
              />
              {pageNumbers}
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) =>
                    prev < Math.ceil(filteredYemekler.length / yemeklerPerPage) ? prev + 1 : prev
                  )
                }
                disabled={currentPage === Math.ceil(filteredYemekler.length / yemeklerPerPage)}
              />
            </Pagination>
          </div>
        )}
      </section>

      <div className="yemek-listesi-shopping mt-5">
        <h3 className="text-center mb-4">
          {selectedMonth === 'all' ? 'Tüm Aylar İçin' : `${selectedMonth} İçin`} Alışveriş Listesi
        </h3>
        {alisverisListesi.length > 0 ? (
          <>
            <div className="row row-cols-1 row-cols-md-3 g-3 yemek-listesi-shopping-grid">
              {currentShoppingItems.map((item, index) => (
                <div key={index} className="col">
                  <div className="yemek-listesi-shopping-item d-flex justify-content-between align-items-center p-2">
                    <span>{item.malzeme}</span>
                    <span>{item.miktar} adet</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center gap-2 mb-3">
              <Button variant="primary" onClick={copyAlisverisListesi}>
                Listeyi Kopyala
              </Button>
              <Button variant="success" onClick={downloadAlisverisListesi}>
                Listeyi İndir
              </Button>
            </div>
            {alisverisListesi.length > shoppingItemsPerPage && (
              <div className="yemek-listesi-shopping-pagination d-flex justify-content-center">
                <Pagination className="yemek-listesi-pagination-custom">
                  <Pagination.Prev
                    onClick={() => setShoppingPage((prev) => (prev > 1 ? prev - 1 : prev))}
                    disabled={shoppingPage === 1}
                  />
                  {shoppingPageNumbers}
                  <Pagination.Next
                    onClick={() =>
                      setShoppingPage((prev) =>
                        prev < Math.ceil(alisverisListesi.length / shoppingItemsPerPage) ? prev + 1 : prev
                      )
                    }
                    disabled={shoppingPage === Math.ceil(alisverisListesi.length / shoppingItemsPerPage)}
                  />
                </Pagination>
              </div>
            )}
          </>
        ) : (
          <p className="yemek-listesi-empty text-center">Alışveriş listesi boş.</p>
        )}
      </div>
    </div>
  );
};

export default YemekListesi;